import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  createRecipe,
  searchIngredients,
  getActiveChefs,
  Ingredient,
  Chef,
} from '../adminApi';

interface IngredientRow {
  ingredient: Ingredient | null;
  quantity: number | '';
  unit: string;
}

interface StepRow {
  header: string;
  description: string;
}

const RecipeCreator: React.FC = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prepTime, setPrepTime] = useState<number | ''>('');
  const [baseServings, setBaseServings] = useState<number | ''>(4);
  const [imageUrl, setImageUrl] = useState('');
  const [chefId, setChefId] = useState<number | ''>('');
  const [activeChefs, setActiveChefs] = useState<Chef[]>([]);

  // Ingredients
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [ingredientOptions, setIngredientOptions] = useState<Ingredient[]>([]);
  const [ingredientLoading, setIngredientLoading] = useState(false);

  // Steps
  const [steps, setSteps] = useState<StepRow[]>([]);

  useEffect(() => {
    const fetchChefs = async () => {
      try {
        const chefs = await getActiveChefs();
        setActiveChefs(chefs);
      } catch {
        // Non-critical
      }
    };
    fetchChefs();
  }, []);

  const handleIngredientSearch = async (query: string) => {
    if (query.length < 2) return;
    setIngredientLoading(true);
    try {
      const result = await searchIngredients({ query, limit: 20 });
      setIngredientOptions(result.ingredients || []);
    } catch {
      // Ignore search errors
    } finally {
      setIngredientLoading(false);
    }
  };

  const addIngredientRow = () => {
    setIngredients([...ingredients, { ingredient: null, quantity: '', unit: '' }]);
  };

  const removeIngredientRow = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const updateIngredientRow = (index: number, updates: Partial<IngredientRow>) => {
    setIngredients(ingredients.map((row, i) => (i === index ? { ...row, ...updates } : row)));
  };

  const addStepRow = () => {
    setSteps([...steps, { header: '', description: '' }]);
  };

  const removeStepRow = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const updateStepRow = (index: number, updates: Partial<StepRow>) => {
    setSteps(steps.map((row, i) => (i === index ? { ...row, ...updates } : row)));
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Recipe name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const validIngredients = ingredients
        .filter((row) => row.ingredient && row.quantity && row.unit)
        .map((row) => ({
          ingredient_id: row.ingredient!.ingredient_id,
          quantity: Number(row.quantity),
          unit: row.unit,
        }));

      const validSteps = steps
        .filter((row) => row.description.trim())
        .map((row, i) => ({
          step_number: i + 1,
          description: row.description,
          header: row.header || undefined,
        }));

      const recipe = await createRecipe({
        name: name.trim(),
        description: description.trim() || undefined,
        prep_time: prepTime || undefined,
        base_servings: baseServings || undefined,
        image_url: imageUrl.trim() || undefined,
        chef_id: chefId || null,
        ingredients: validIngredients.length > 0 ? validIngredients : undefined,
        steps: validSteps.length > 0 ? validSteps : undefined,
      });

      navigate(`/admin/recipes/${recipe.recipe_id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create recipe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/admin/recipes')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" flexGrow={1}>
          Create Recipe
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleCreate}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Creating...' : 'Create Recipe'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="Recipe Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Prep Time (minutes)"
                  type="number"
                  value={prepTime}
                  onChange={(e) => setPrepTime(e.target.value ? parseInt(e.target.value) : '')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Base Servings"
                  type="number"
                  value={baseServings}
                  onChange={(e) => setBaseServings(e.target.value ? parseInt(e.target.value) : '')}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Chef Attribution</InputLabel>
                  <Select
                    value={chefId}
                    label="Chef Attribution"
                    onChange={(e) => setChefId(e.target.value as number | '')}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {activeChefs.map((c) => (
                      <MenuItem key={c.chef_id} value={c.chef_id}>
                        {c.name} ({c.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  label="Image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Ingredients */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Ingredients ({ingredients.length})</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addIngredientRow}>
                Add Ingredient
              </Button>
            </Box>
            {ingredients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No ingredients added yet. Click "Add Ingredient" to start.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {ingredients.map((row, index) => (
                  <Box key={index} display="flex" gap={1} alignItems="center">
                    <Autocomplete
                      sx={{ flexGrow: 1, minWidth: 200 }}
                      options={ingredientOptions}
                      getOptionLabel={(opt) => opt.name}
                      value={row.ingredient}
                      onChange={(_, value) => updateIngredientRow(index, { ingredient: value })}
                      onInputChange={(_, value) => handleIngredientSearch(value)}
                      loading={ingredientLoading}
                      isOptionEqualToValue={(opt, val) => opt.ingredient_id === val.ingredient_id}
                      renderInput={(params) => (
                        <TextField {...params} label="Ingredient" size="small" />
                      )}
                    />
                    <TextField
                      label="Qty"
                      type="number"
                      size="small"
                      value={row.quantity}
                      onChange={(e) =>
                        updateIngredientRow(index, {
                          quantity: e.target.value ? parseFloat(e.target.value) : '',
                        })
                      }
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="Unit"
                      size="small"
                      value={row.unit}
                      onChange={(e) => updateIngredientRow(index, { unit: e.target.value })}
                      sx={{ width: 120 }}
                      placeholder="e.g., cups"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeIngredientRow(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Steps */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Steps ({steps.length})</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addStepRow}>
                Add Step
              </Button>
            </Box>
            {steps.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No steps added yet. Click "Add Step" to start.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {steps.map((row, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mt: 1.5, minWidth: 30, textAlign: 'center' }}
                    >
                      {index + 1}.
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <TextField
                        label="Header (optional)"
                        size="small"
                        value={row.header}
                        onChange={(e) => updateStepRow(index, { header: e.target.value })}
                        fullWidth
                        sx={{ mb: 1 }}
                        placeholder="e.g., Prep, Cooking"
                      />
                      <TextField
                        label="Description"
                        size="small"
                        value={row.description}
                        onChange={(e) => updateStepRow(index, { description: e.target.value })}
                        fullWidth
                        multiline
                        rows={2}
                        required
                      />
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeStepRow(index)}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {imageUrl && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Image Preview
              </Typography>
              <Box
                component="img"
                src={imageUrl}
                alt={name}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  maxHeight: 200,
                  objectFit: 'cover',
                }}
                onError={(e: any) => {
                  e.target.style.display = 'none';
                }}
              />
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tips
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After creating the recipe, you'll be taken to the full editor where you can add
              nutrition info, tags, and make further edits.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecipeCreator;
